import axios, { AxiosError, AxiosResponse } from 'axios';
import User from '../../../models/User';
import { interceptor } from '../../../utils/axios.interceptor';
import {
  loginEndpoint,
  logOutEndpoint,
  forgotPasswordEndPoint,
  userEndPoint,
  resetPasswordEndPoint,
  registerEndpoint,
} from '../../../utils/endPoints';

export const authInstance = axios.create();
interceptor(authInstance);
export class AuthenticationService {
  /**
   * Used to create the login
   * @param user : User email
   * @param password : User password
   * @returns code responded by server or 400
   */
  async userLogIn(
    user: string,
    password: string,
  ): Promise<{ status: number; data: User }> {
    try {
      const response = await axios.post(
        loginEndpoint,
        {
          username: user,
          password: password,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      let result;
      if (response.status === 200) {
        result = { status: response.status, data: response.data.user };
        localStorage.setItem('refreshToken', response.data.refresh_token);
        localStorage.setItem('accessToken', response.data.access_token);
        localStorage.setItem(
          'userName',
          response.data.user.nombre + ' ' + response.data.user.apellido,
        );
        localStorage.setItem('currentUser', JSON.stringify(response.data.user))
      } else {
        result = { status: response.status, data: [] };
      }
      return result;
    } catch (error) {
      const err = error as AxiosError;
      return {
        status: err.response?.status as number,
        data: {}
      };
    }
  }
  /**
   * Used to logOut the user
   * @returns code responded by server or 400
   */
  async logOut(): Promise<any> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('no token supplied');
      }
      const response = await authInstance.post(logOutEndpoint, null, {
        headers: { 'X-Refresh-Token': refreshToken },
      });
      return response.data;
    } catch (error) {
      return { code: 400 };
    }
  }
  /**
   * Used to send a forgot password request by the user email
   * @returns code responded by server or 400
   */
  async forgotPassword(
    email: string,
  ): Promise<{ status: boolean; data: AxiosResponse | unknown }> {
    try {
      let result;
      const response = await axios.get(
        `${forgotPasswordEndPoint}?email=${email}`,
      );
      if (response.status === 200) {
        result = { status: true, data: response.data };
      } else {
        result = { status: false, data: response.data };
      }
      return result;
    } catch (error) {
      const err = error as AxiosError;
      return {
        status: false,
        data: err.response?.data,
      };
    }
  }
  /**
   * Used to get the user profile
   * @returns user object
   */
  async getUserProfile(): Promise<{
    status: boolean;
    data: AxiosResponse | unknown;
  }> {
    try {
      const response = await authInstance.get(`${userEndPoint}/perfil`);
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      const err = error as AxiosError;
      return {
        status: false,
        data: err.response?.data,
      };
    }
  }
  /**
   * Used to update the user by id
   * @returns user object updated
   */
  async userPasswordCreate(
    token: string,
    password: string,
  ): Promise<{
    status: boolean;
    data: AxiosResponse | unknown;
  }> {
    try {
      const response = await axios.post(
        `${registerEndpoint}`,
        {
          password: password,
        },
        { params: { token: token } },
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      const err = error as AxiosError;
      return {
        status: false,
        data: err.response?.data,
      };
    }
  }
  /**
   * Used to send email by user id to invite the user to Invap
   * @returns user object updated
   */
  async inviteUser(userId: string): Promise<{
    status: boolean;
    data: AxiosResponse | unknown;
  }> {
    try {
      const response = await authInstance.post(
        `${userEndPoint}/invite/${userId}`,
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      const err = error as AxiosError;
      return {
        status: false,
        data: err.response?.data,
      };
    }
  }
  /**
   * Used to reset user password
   * @returns success
   */
  async resetPassword(
    token: string,
    password: string,
  ): Promise<{
    status: boolean;
    data: AxiosResponse | unknown;
  }> {
    try {
      const response = await axios.post(
        `${resetPasswordEndPoint}`,
        {
          password: password,
        },
        { params: { token: token } },
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      const err = error as AxiosError;
      return {
        status: false,
        data: err.response?.data,
      };
    }
  }
}
